import { useCallback } from 'react'
import { useStore } from 'react-redux'
import { useAppDispatch, useAppSelector } from '../../hooks'
import { authActions } from './actions'
import {
  selectUser,
  selectIsAuthenticated,
  selectAuthLoading,
  selectIsCheckingAuth,
  selectAuthError
} from './selectors'
import { User } from '../../../types/api'
import { RegisterData } from './types'
import { RootState } from '../../types'

/**
 * Hook para usar funcionalidades de Auth com Redux
 * Mantém compatibilidade com a interface antiga do AuthContext
 */
export const useAuth = () => {
  const dispatch = useAppDispatch()
  // eslint-disable-next-line no-unused-vars -- usado em register() (store.subscribe/getState)
  const store = useStore<RootState>()
  const user = useAppSelector(selectUser)
  const isAuthenticated = useAppSelector(selectIsAuthenticated)
  const isLoading = useAppSelector(selectAuthLoading)
  const isCheckingAuth = useAppSelector(selectIsCheckingAuth)
  const error = useAppSelector(selectAuthError)

  const login = useCallback(async (emailOrCpf: string, password: string): Promise<User | null> => {
    return new Promise((resolve) => {
      dispatch(authActions.loginRequest({ emailOrCpf, password }))
      
      // Aguardar resultado (será tratado pela saga)
      // Por enquanto, retornamos null e o componente deve verificar o estado
      // Em uma implementação mais avançada, poderíamos usar um canal do saga
      resolve(null)
    })
  }, [dispatch])

  const logout = useCallback(() => {
    dispatch(authActions.logoutRequest())
  }, [dispatch])

  const register = useCallback(async (userData: RegisterData): Promise<boolean> => {
    dispatch(authActions.registerRequest(userData))
    return new Promise((resolve) => {
      const unsubscribe = store.subscribe(() => {
        const state = store.getState()
        const result = state.auth.registerResult
        if (result === true || result === false) {
          unsubscribe()
          dispatch(authActions.clearRegisterResult())
          resolve(result)
        }
      })
    })
  }, [dispatch, store])

  const updateUser = useCallback((userData: Partial<User>) => {
    dispatch(authActions.updateUser(userData))
  }, [dispatch])

  // Combinar isLoading e isCheckingAuth para compatibilidade
  const combinedIsLoading = isLoading || isCheckingAuth

  return {
    user,
    isAuthenticated,
    isLoading: combinedIsLoading,
    login,
    logout,
    register,
    updateUser,
    error
  }
}

